/**
 * Herkenning van "versie-skew"-fouten: de app is bijgewerkt (of een chunk is
 * dubbel geladen) terwijl iemand aan het werk was. Zulke fouten zijn met
 * opnieuw renderen niet te verhelpen; alleen een volledige herlaad haalt weer
 * één samenhangende versie op. Pure functies, zodat de ErrorBoundary-logica
 * zonder DOM te testen is.
 *
 * Casus Pieter 01-10-2026 (meldingen 02220dcb/cd3f7f02): "No QueryClient set"
 * in de werkbon. Oorzaak was een dynamic `import()` naar een module uit de
 * entry-chunk; met Vercel skew-protection (`?dpl=`) laadde de browser daardoor
 * de hele entry-chunk een tweede keer, inclusief een tweede React-Query-context.
 */

const SKEW_PATRONEN: RegExp[] = [
  /valid JavaScript MIME type/i,
  /dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading (CSS )?chunk/i,
  /text\/html/i,
  /No QueryClient set/i,
];

/** Fout die wijst op een verouderde of dubbel geladen bundel. */
export function isVersieSkewFout(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: unknown; message?: unknown };
  const msg = `${typeof e.name === 'string' ? e.name : ''} ${
    typeof e.message === 'string' ? e.message : String(error)
  }`;
  return SKEW_PATRONEN.some((re) => re.test(msg));
}

export const HERLAAD_SLEUTEL = 'pracht_eb_reload';
/** Binnen dit venster herladen we niet nóg een keer (tegen een ververslus). */
export const HERLAAD_VENSTER_MS = 15_000;

export interface HerlaadGeheugen {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Eén automatische herlaad per venster. Geeft `true` als de aanroeper nu mag
 * herladen (en registreert dat moment); `false` als er net al herladen is —
 * dan hoort de gebruiker een eerlijke melding te zien in plaats van een lus.
 */
export function magEenmaligHerladen(
  geheugen: HerlaadGeheugen,
  nu: number = Date.now(),
  venster: number = HERLAAD_VENSTER_MS,
): boolean {
  const vorige = geheugen.getItem(HERLAAD_SLEUTEL);
  if (vorige) {
    const t = parseInt(vorige, 10);
    if (!Number.isNaN(t) && nu - t < venster) return false;
  }
  geheugen.setItem(HERLAAD_SLEUTEL, String(nu));
  return true;
}
